import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion";
import { ARTICLES, WHATSAPP_LINK } from "@/data";
import { Navbar, Footer, FloatingCTA } from "@/components/Layout";
import { ArrowLeft, Clock, Tag, ChevronRight } from "lucide-react";

// ── Claude / Skywork AI Articles Widget ──────────────────────────────────────
// Articles are posted to https://api.skywork.ai/v1/articles via the Python agent.
// The frontend fetches them using VITE_SKYWORK_API_KEY (set in .env).

const SKYWORK_API = "https://api.skywork.ai/v1/articles";
const SKYWORK_KEY = import.meta.env.VITE_SKYWORK_API_KEY as string | undefined;

interface SkyworkArticle {
  id: string;
  title: string;
  slug?: string;
  content: string;
  meta_description?: string;
  status: string;
  created_at?: string;
}

function ClaudeArticlesWidget() {
  const [articles, setArticles] = useState<SkyworkArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!SKYWORK_KEY) {
      setError("API key not configured");
      setLoading(false);
      return;
    }
    fetch(SKYWORK_API, {
      headers: { Authorization: `Bearer ${SKYWORK_KEY}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        // Handle both array response and { articles: [...] } shape
        const list: SkyworkArticle[] = Array.isArray(data)
          ? data
          : data.articles ?? data.data ?? [];
        setArticles(list.filter((a) => a.status === "published"));
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Don't render the section at all if no key and no articles
  if (!SKYWORK_KEY && articles.length === 0) return null;

  return (
    <div className="mt-16">
      <div className="border-t border-border pt-14 mb-10 text-center">
        <h2
          className="text-2xl md:text-3xl font-bold text-foreground mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Latest Legal Insights
        </h2>
        <p className="text-muted-foreground text-sm">
          AI-generated articles on Singapore civil and criminal law — powered by Claude
        </p>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error && !loading && (
        <div className="max-w-xl mx-auto text-center py-10 text-muted-foreground text-sm">
          {error === "API key not configured" ? (
            <p>Add <code className="bg-secondary px-1.5 py-0.5 rounded text-xs">VITE_SKYWORK_API_KEY</code> to your <code className="bg-secondary px-1.5 py-0.5 rounded text-xs">.env</code> file to display Claude-generated articles here.</p>
          ) : (
            <p>Could not load articles: {error}</p>
          )}
        </div>
      )}

      {!loading && articles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {articles.map((article) => (
            <motion.article
              key={article.id}
              variants={staggerItem}
              className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
              onClick={() =>
                setExpanded(expanded === article.id ? null : article.id)
              }
            >
              <div className="h-1.5 w-full bg-accent" />
              <div className="p-7">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent/10 text-accent border border-accent/20">
                    ✦ Claude AI
                  </span>
                  {article.created_at && (
                    <span className="text-xs text-muted-foreground ml-auto">
                      {new Date(article.created_at).toLocaleDateString("en-SG", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  )}
                </div>

                <h3
                  className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {article.title}
                </h3>

                {/* Meta description preview (SEO excerpt) */}
                {article.meta_description && (
                  <p className="text-muted-foreground text-sm italic mb-3 border-l-2 border-accent/40 pl-3">
                    {article.meta_description}
                  </p>
                )}

                {/* Preview / expanded content */}
                <AnimatePresence initial={false}>
                  {expanded === article.id ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line mb-4">
                        {article.content}
                      </p>
                    </motion.div>
                  ) : (
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-3">
                      {article.content}
                    </p>
                  )}
                </AnimatePresence>

                <div className="flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:text-accent transition-colors">
                  {expanded === article.id ? "Show less ↑" : "Read more ↓"}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Soro AI Blog Widget ───────────────────────────────────────────────────────
// Exact embed code from Soro:
//   <div id="soro-blog"></div>
//   <script src="https://app.trysoro.com/api/embed/3d2402c6-e8ce-4efa-8b6f-c5660a397523" defer></script>

const SORO_SCRIPT_URL =
  "https://app.trysoro.com/api/embed/3d2402c6-e8ce-4efa-8b6f-c5660a397523";

function SoroBlogWidget() {
  useEffect(() => {
    // Only inject once — Soro's script self-initialises on load
    if (document.getElementById("soro-embed-script")) return;

    const script = document.createElement("script");
    script.id = "soro-embed-script";
    script.src = SORO_SCRIPT_URL;
    script.defer = true; // matches the exact API code: <script ... defer>
    document.head.appendChild(script); // inject into <head> as Soro expects

    return () => {
      // no cleanup — removing the script causes it not to re-render on back-navigation
    };
  }, []);

  return (
    <div className="mt-16">
      <div className="border-t border-border pt-14 mb-10 text-center">
        <h2
          className="text-2xl md:text-3xl font-bold text-foreground mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          More Legal Articles
        </h2>
        <p className="text-muted-foreground text-sm">
          Regularly updated guides on Singapore civil and criminal law
        </p>
      </div>
      {/* Soro embed target — exact id required by the Soro script */}
      <div id="soro-blog"></div>
    </div>
  );
}

type Article = (typeof ARTICLES)[number];

function ArticleDetail({ article, onBack }: { article: Article; onBack: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      className="max-w-3xl mx-auto"
    >
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 group"
      >
        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        Back to Articles
      </button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              article.category === "Criminal Law"
                ? "bg-red-100 text-red-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            {article.category}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock size={12} />
            {article.readTime}
          </span>
          <span className="text-xs text-muted-foreground">{article.date}</span>
        </div>
        <h1
          className="text-3xl md:text-4xl font-bold text-foreground leading-tight mb-4"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {article.title}
        </h1>
        <p className="text-muted-foreground text-lg leading-relaxed">{article.excerpt}</p>
      </div>

      {/* Divider */}
      <div className="border-t border-border mb-8" />

      {/* Content sections */}
      <div className="space-y-8">
        {article.content.map((section, idx) => (
          <div key={idx}>
            <h2
              className="text-xl font-bold text-foreground mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {section.heading}
            </h2>
            <div className="text-muted-foreground leading-relaxed text-sm md:text-base whitespace-pre-line">
              {section.body}
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-12 bg-primary rounded-2xl p-8 text-primary-foreground text-center">
        <h3
          className="text-2xl font-bold mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Need Legal Advice?
        </h3>
        <p className="text-primary-foreground/70 mb-6 text-sm">
          Our lawyers handle both civil and criminal claim trials in Singapore. Get a free, confidential consultation today.
        </p>
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-accent text-sm font-bold px-8 py-3 rounded-md hover:bg-accent/90 transition-all duration-200 hover:scale-105"
          style={{ color: "#0a1628" }}
        >
          💬 Free WhatsApp Consultation
        </a>
      </div>
    </motion.div>
  );
}

export default function Articles() {
  const [selected, setSelected] = useState<Article | null>(null);
  const [filter, setFilter] = useState<"all" | "Civil Law" | "Criminal Law">("all");

  const filtered =
    filter === "all" ? ARTICLES : ARTICLES.filter((a) => a.category === filter);

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background">
        {/* Page Hero */}
        <section className="bg-primary pt-28 pb-14">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              <motion.span
                variants={staggerItem}
                className="inline-block bg-accent/20 text-accent border border-accent/30 text-xs font-semibold px-3 py-1 rounded-full mb-4 tracking-wide uppercase"
              >
                Legal Knowledge Hub
              </motion.span>
              <motion.h1
                variants={staggerItem}
                className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Legal Articles & Guides
              </motion.h1>
              <motion.p
                variants={staggerItem}
                className="text-primary-foreground/70 max-w-xl mx-auto text-lg"
              >
                Practical guides on civil and criminal claim trials in Singapore — written by our lawyers to help you understand your rights.
              </motion.p>
            </motion.div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <AnimatePresence mode="wait">
              {selected ? (
                <ArticleDetail
                  key="detail"
                  article={selected}
                  onBack={() => setSelected(null)}
                />
              ) : (
                <motion.div
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {/* Filter */}
                  <div className="flex flex-wrap justify-center gap-2 mb-10">
                    {(["all", "Civil Law", "Criminal Law"] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setFilter(tab)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
                          filter === tab
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                        }`}
                      >
                        {tab === "all"
                          ? "All Articles"
                          : tab === "Civil Law"
                          ? "⚖️ Civil Law"
                          : "🔒 Criminal Law"}
                      </button>
                    ))}
                  </div>

                  {/* Article grid */}
                  <motion.div
                    key={filter}
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto"
                  >
                    {filtered.map((article) => (
                      <motion.article
                        key={article.id}
                        variants={staggerItem}
                        className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                        onClick={() => {
                          setSelected(article);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        {/* Category colour strip */}
                        <div
                          className={`h-1.5 w-full ${
                            article.category === "Criminal Law"
                              ? "bg-red-500"
                              : "bg-blue-500"
                          }`}
                        />
                        <div className="p-7">
                          <div className="flex items-center gap-3 mb-4">
                            <span
                              className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                                article.category === "Criminal Law"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              <Tag size={10} />
                              {article.category}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock size={11} />
                              {article.readTime}
                            </span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {article.date}
                            </span>
                          </div>

                          <h2
                            className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors leading-snug"
                            style={{ fontFamily: "'Playfair Display', serif" }}
                          >
                            {article.title}
                          </h2>
                          <p className="text-muted-foreground text-sm leading-relaxed mb-5 line-clamp-3">
                            {article.excerpt}
                          </p>

                          <div className="flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:text-accent transition-colors">
                            Read Article
                            <ChevronRight
                              size={16}
                              className="group-hover:translate-x-1 transition-transform"
                            />
                          </div>
                        </div>
                      </motion.article>
                    ))}
                  </motion.div>

                  {/* Bottom CTA */}
                  <motion.div
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="mt-16 text-center"
                  >
                    <p className="text-muted-foreground mb-4 text-sm">
                      Have a specific legal question? Get direct advice from our lawyers.
                    </p>
                    <a
                      href={WHATSAPP_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-primary text-primary-foreground text-sm font-bold px-8 py-3 rounded-md hover:bg-primary/90 transition-all duration-200 hover:scale-105"
                    >
                      💬 Free Consultation
                    </a>
                  </motion.div>

                  {/* Soro AI-generated articles feed */}
                  <SoroBlogWidget />

                  {/* Claude / Skywork AI-generated articles feed */}
                  <ClaudeArticlesWidget />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </>
  );
}
